/** @jsx h */
/** @jsxFrag Fragment */
import { h, createContext } from 'preact'
import { useReducer, useContext, useEffect } from 'preact/hooks'
import type { ComponentChildren, JSX } from 'preact'
import { State, Actions, reducer, initialState } from './reducer'
import * as persister from './persist'
import { throttle } from '../shared/utils'

type ContextProps = [State, (action: Actions) => void]

export const StateContext = createContext<ContextProps | undefined>(undefined)

const throttledPersist = throttle(persister.persist, 1000) as (s: State) => void

type ProviderProps = {
    children?: ComponentChildren
    customInitialState?: Partial<State>
}

export function StateProvider({ children, customInitialState }: ProviderProps): JSX.Element {
    const [state, dispatch] = useReducer(reducer, { ...initialState, ...customInitialState })

    useEffect(() => {
        throttledPersist(state)
    }, [state])

    const Provider = (StateContext.Provider as unknown) as (props: {
        value: ContextProps
        children?: ComponentChildren
    }) => JSX.Element

    return h(Provider, { value: [state, dispatch] }, children as any)
}

export function useStateValue(): ContextProps {
    const ctx = useContext(StateContext)
    if (!ctx) {
        throw new Error('Cannot use `useStateValue` outside of a StateProvider')
    }
    return ctx
}
